import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Plus,
  MoreVertical,
  Archive,
  Trash2,
  Power,
  Activity,
  Moon,
  AlertCircle,
  Target,
} from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useTheme } from '../../contexts/ThemeContext';
import { VisibilityIndicator } from '../stream';
import type { VisibilityLevel, AccessMember } from '../stream';

export interface StreamItem {
  id: string;
  name: string;
  status: 'active' | 'sleeping' | 'attention' | 'standby' | 'archived';
  progress: {
    completed: number;
    total: number;
  };
  lastUpdated: string;
  archived?: boolean;
  alertCount?: number;
  taskCount?: number;
  agentCount?: number;
  visibility?: VisibilityLevel;
  accessList?: AccessMember[];
  usage?: {
    total_tokens: number;
  };
  /** Spec 124 Phase 0.5: sandbox backend indicator */
  sandboxMachineType?: 'sprite' | 'standard_machine' | null;
  sandboxMachineRegion?: string | null;
}

interface StreamListProps {
  streams: StreamItem[];
  selectedId?: string;
  onSelect: (id: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onNewStream: () => void;
  onArchiveStream: (id: string) => void;
  onToggleStreamActive: (id: string) => void;
  onDeleteStream: (id: string) => void;
  onVisibilityChange?: (id: string, visibility: VisibilityLevel) => void;
  onManageAccess?: (id: string) => void;
}

export function StreamList({
  streams,
  selectedId,
  onSelect,
  isCollapsed,
  onToggleCollapse,
  onNewStream,
  onArchiveStream,
  onToggleStreamActive,
  onDeleteStream,
  onVisibilityChange,
  onManageAccess,
}: StreamListProps) {
  const { locale, isCommand } = useTheme();
  const [contextMenu, setContextMenu] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const activeStreams = streams.filter((s) => !s.archived && s.status !== 'archived');
  const archivedStreams = streams.filter((s) => s.archived || s.status === 'archived');

  const getStatusColor = (status: StreamItem['status']) => {
    switch (status) {
      case 'active':
        return 'bg-primary-500';
      case 'attention':
        return 'bg-warning-500';
      case 'sleeping':
      case 'standby':
        return 'bg-purple-500';
      case 'archived':
        return 'bg-text-muted';
      default:
        return 'bg-text-muted';
    }
  };

  const getStatusIcon = (status: StreamItem['status']) => {
    switch (status) {
      case 'active':
        return <Activity className="w-3 h-3 text-primary-500" />;
      case 'attention':
        return <AlertCircle className="w-3 h-3 text-warning-500" />;
      case 'sleeping':
      case 'standby':
        return <Moon className="w-3 h-3 text-purple-500" />;
      default:
        return null;
    }
  };

  const formatTokens = (tokens: number) => {
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
    if (tokens >= 1000) return `${Math.round(tokens / 1000)}K`;
    return tokens.toString();
  };

  return (
    <motion.aside
      className="relative h-full bg-surface-800 border-r border-surface-600 flex flex-col"
      animate={{ width: isCollapsed ? 64 : 280 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      {/* Header */}
      <div className="p-4 border-b border-surface-600">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <div className="flex items-center gap-2">
              {isCommand && <Target className="w-5 h-5 text-primary-500" />}
              <h2
                className={`text-sm font-semibold ${
                  isCommand
                    ? 'font-display uppercase tracking-wider text-primary-400'
                    : 'text-text-secondary uppercase tracking-wider'
                }`}
              >
                {locale.streams}
              </h2>
            </div>
          )}
          <button
            onClick={onToggleCollapse}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="p-1.5 hover:bg-surface-700 rounded text-text-secondary hover:text-primary-400 transition-colors"
          >
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* New Stream Button */}
      <div className="p-3 border-b border-surface-600">
        {isCommand ? (
          <button
            onClick={onNewStream}
            className={`
              w-full flex items-center justify-center gap-2
              px-3 py-2 rounded
              bg-surface-700 border border-surface-600
              text-text-secondary hover:text-primary-400 hover:border-primary-500/50
              transition-all duration-200
              ${isCollapsed ? 'px-2' : ''}
            `}
          >
            <Plus className="w-4 h-4" />
            {!isCollapsed && (
              <span className="text-xs font-semibold uppercase tracking-wider">{locale.newStream}</span>
            )}
          </button>
        ) : (
          <Button className="w-full justify-center" size="sm" onClick={onNewStream}>
            <Plus className="w-4 h-4 mr-2" />
            {!isCollapsed && locale.newStream}
          </Button>
        )}
      </div>

      {/* Stream List */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {activeStreams.map((stream) => (
          <div key={stream.id} className="relative group">
            <div
              role="button"
              tabIndex={0}
              onClick={() => onSelect(stream.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(stream.id);
                }
              }}
              className={`
                w-full text-left rounded transition-all duration-200 cursor-pointer
                ${
                  selectedId === stream.id
                    ? 'bg-primary-500/10 border-l-2 border-primary-500'
                    : 'hover:bg-surface-700 border-l-2 border-transparent'
                }
                ${isCollapsed ? 'p-2' : 'p-3'}
              `}
            >
              <div className="flex items-start gap-3">
                {/* Status Indicator */}
                <div className="relative mt-1.5">
                  <div className={`w-2 h-2 rounded-full ${getStatusColor(stream.status)}`} />
                  {stream.status === 'active' && (
                    <div
                      className={`absolute inset-0 w-2 h-2 rounded-full ${getStatusColor(stream.status)} animate-ping opacity-75`}
                    />
                  )}
                </div>

                {!isCollapsed && (
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className={`text-sm font-semibold text-text-primary truncate ${
                          isCommand ? 'uppercase tracking-wide' : ''
                        }`}
                      >
                        {stream.name}
                      </span>
                      <div className="flex items-center gap-1">
                        {!isCommand && getStatusIcon(stream.status)}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setContextMenu(contextMenu === stream.id ? null : stream.id);
                          }}
                          className="p-1 opacity-0 group-hover:opacity-100 hover:bg-surface-600 rounded text-text-muted hover:text-text-primary transition-all"
                        >
                          <MoreVertical className="w-3 h-3" />
                        </button>
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <div
                        className={`flex items-center gap-2 text-text-muted ${
                          isCommand ? 'text-[10px] font-mono' : 'text-xs'
                        }`}
                      >
                        <span>
                          {stream.taskCount ?? stream.progress.total} {locale.tasks.toLowerCase()}
                        </span>
                        {stream.agentCount !== undefined && (
                          <>
                            <span className="text-surface-500">|</span>
                            <span>
                              {stream.agentCount} {locale.agents.toLowerCase()}
                            </span>
                          </>
                        )}
                        {stream.usage && stream.usage.total_tokens > 0 && (
                          <>
                            <span className="text-surface-500">|</span>
                            <span>{formatTokens(stream.usage.total_tokens)} tokens</span>
                          </>
                        )}
                      </div>

                      {/* Visibility Indicator */}
                      {stream.visibility && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <VisibilityIndicator
                            level={stream.visibility}
                            editable={!!onVisibilityChange}
                            onChange={(newVisibility) => onVisibilityChange?.(stream.id, newVisibility)}
                            onRestrictedClick={() => onManageAccess?.(stream.id)}
                            showLabel={false}
                            size="sm"
                          />
                        </div>
                      )}
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1 bg-surface-600 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            stream.status === 'attention' || (stream.alertCount && stream.alertCount > 0)
                              ? 'bg-warning-500'
                              : 'bg-primary-500'
                          }`}
                          style={{
                            width: `${(stream.progress.completed / stream.progress.total) * 100}%`,
                          }}
                        />
                      </div>
                      <span
                        className={`text-text-muted tabular-nums ${isCommand ? 'text-[9px] font-mono' : 'text-[10px]'}`}
                      >
                        {stream.progress.completed}/{stream.progress.total}
                      </span>
                    </div>

                    {stream.sandboxMachineType && (
                      <span
                        className={`mt-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                          stream.sandboxMachineType === 'standard_machine'
                            ? 'bg-primary-500/10 text-primary-400 border-primary-500/20'
                            : 'bg-purple-500/10 text-purple-400 border-purple-500/20'
                        }`}
                      >
                        {stream.sandboxMachineType === 'standard_machine' ? 'Fly' : 'Sprites'}
                        {stream.sandboxMachineRegion ? ` · ${stream.sandboxMachineRegion}` : ''}
                      </span>
                    )}
                    {stream.alertCount !== undefined && stream.alertCount > 0 && (
                      <Badge variant="danger" className="mt-2">
                        {stream.alertCount} {locale.attention.toLowerCase()}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Context Menu */}
            <AnimatePresence>
              {contextMenu === stream.id && !isCollapsed && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute right-2 top-full mt-1 z-50 w-40 bg-surface-700 border border-surface-600 rounded-lg shadow-xl overflow-hidden"
                >
                  <button
                    onClick={() => {
                      onToggleStreamActive(stream.id);
                      setContextMenu(null);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-secondary hover:bg-surface-600 hover:text-text-primary transition-colors"
                  >
                    <Power className="w-3 h-3" />
                    {stream.status === 'active' ? (isCommand ? 'Set Standby' : 'Pause') : 'Activate'}
                  </button>
                  <button
                    onClick={() => {
                      onArchiveStream(stream.id);
                      setContextMenu(null);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-secondary hover:bg-surface-600 hover:text-text-primary transition-colors"
                  >
                    <Archive className="w-3 h-3" />
                    {locale.archiveStream}
                  </button>
                  <button
                    onClick={() => {
                      onDeleteStream(stream.id);
                      setContextMenu(null);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-danger-400 hover:bg-surface-600 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    {locale.deleteStream}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>

      {/* Archived section */}
      {archivedStreams.length > 0 && !isCollapsed && (
        <div className="border-t border-surface-600">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="w-full flex items-center justify-between p-4 hover:bg-surface-700/50 transition-colors"
          >
            <span className={`text-sm font-medium text-text-secondary ${isCommand ? 'uppercase tracking-wider text-xs' : ''}`}>
              Archived ({archivedStreams.length})
            </span>
            {showArchived ? <ChevronUp className="w-4 h-4 text-text-muted" /> : <ChevronDown className="w-4 h-4 text-text-muted" />}
          </button>
          {showArchived && (
            <div className="px-3 pb-3 space-y-1">
              {archivedStreams.map((stream) => (
                <button
                  key={stream.id}
                  onClick={() => onSelect(stream.id)}
                  className="w-full text-left p-2 rounded-lg text-text-muted hover:bg-surface-700/50 hover:text-text-secondary transition-colors text-sm"
                >
                  {stream.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      {!isCollapsed && (
        <div className="p-3 border-t border-surface-600">
          <div
            className={`text-center ${
              isCommand ? 'text-[10px] font-mono text-text-muted uppercase tracking-wider' : 'text-xs text-text-muted'
            }`}
          >
            {isCommand ? 'Imperial Command v2.0' : 'All systems operational'}
          </div>
        </div>
      )}

      {/* Click outside to close context menu */}
      {contextMenu && <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} />}
    </motion.aside>
  );
}

// Backwards compatibility exports
export { StreamList as MissionList };
